/**
 * steps.ts — the bottom-left step indicator shared by the flow (index.ts).
 * Step 1 Choose your base · Step 2 Design your piece · Step 3 Review your piece.
 * The active step shows its number badge + label; reached steps are clickable to
 * navigate back; later steps are dimmed.
 *
 * Every step keeps its label in the DOM (collapsed via a grid 0fr→1fr column) so
 * switching active animates: the old label wipes closed, the new one wipes open,
 * and the badge/pill background-colours transition — no rebuild, no fade.
 */

const el = (tag: string, cls?: string) => { const e = document.createElement(tag); if (cls) e.className = cls; return e; };

export const STEP_LABELS = ['Choose your base', 'Design your piece', 'Review your piece'];

export interface StepIndicator {
  el: HTMLElement;
  setActive: (n: number) => void; // animates the label wipe + colour transition
}

const ITEM_BASE  = 'cfg__step flex items-center rounded-sm transition-[background-color,padding] duration-500 ease-out';
const BADGE_BASE = 'flex items-center justify-center w-6 h-6 rounded-xs text-xs font-semibold shrink-0 transition-colors duration-500 ease-out';
const GRID_BASE  = 'grid transition-[grid-template-columns] duration-500 ease-out';

/** Build the indicator. `active` is 1-based; clicking a reached step calls onGo(n). */
export function buildStepIndicator(active: number, onGo: (n: number) => void): StepIndicator {
  const wrap = el('div', 'cfg__steps inline-flex items-center gap-1.5');
  const parts: { btn: HTMLButtonElement; badge: HTMLElement; grid: HTMLElement }[] = [];

  STEP_LABELS.forEach((label, i) => {
    const n = i + 1;
    const btn = el('button', ITEM_BASE) as HTMLButtonElement;
    btn.type = 'button';
    const badge = el('span', BADGE_BASE);
    badge.textContent = String(n);
    // Collapsing label: grid column 0fr (closed) → 1fr (open); inner clips the text.
    const grid = el('span', GRID_BASE);
    const inner = el('span', 'overflow-hidden min-w-0');
    const lbl = el('span', 'block pl-2 pr-1 text-sm font-semibold uppercase whitespace-nowrap');
    lbl.textContent = label;
    inner.appendChild(lbl);
    grid.appendChild(inner);
    btn.append(badge, grid);
    btn.addEventListener('click', () => onGo(n));
    wrap.appendChild(btn);
    parts.push({ btn, badge, grid });
  });

  function setActive(active: number): void {
    parts.forEach(({ btn, badge, grid }, i) => {
      const n = i + 1;
      const isActive = n === active;
      const reached = n <= active;
      btn.className = `${ITEM_BASE} ` +
        (isActive ? 'pl-1 pr-3 py-1 bg-detail/30 pointer-events-none ' : 'p-1 bg-transparent ') +
        (reached && !isActive ? 'cursor-pointer hover:bg-detail/10 ' : '') +
        (!reached ? 'opacity-45 pointer-events-none ' : '');
      badge.className = `${BADGE_BASE} ` + (isActive ? 'bg-foreground text-background' : 'bg-detail/10 text-foreground');
      grid.className = `${GRID_BASE} ` + (isActive ? 'grid-cols-[1fr]' : 'grid-cols-[0fr]');
    });
  }

  setActive(active);
  return { el: wrap, setActive };
}
